import { CONTROL_TYPES } from '@/components/AppInput.vue';
import {
  COMPARISON_OPERATORS,
  OPERATORS,
  TEXT_MATCH_OPERATORS,
  shiftMonthsIso,
  todayIso,
} from '@/components/filterBar.helpers';
import { EMPLOYEE_PAY_TYPE } from '@/utils/Employee';
import { SHIFT_SOURCE } from '@/utils/Shifts';
import { toTitleCase } from '@/utils/common';

export const SHIFT_DATE_FILTER_KEY = 'worked_on';

// shifts-v2 rejects a window wider than this, so the picker has to stop the user before the request goes out.
export const SHIFT_MAX_RANGE_DAYS = 62;

// The window in its native unit: SHIFT_MAX_RANGE_DAYS is exactly what two consecutive calendar months can span,
// so working in months keeps the same day-of-month on both bounds and can never breach the cap.
export const SHIFT_WINDOW_MONTHS = 2;

// Anchored on one `to` so the pair can't straddle midnight, and shifted by the same helper the snap uses.
export const defaultShiftDateRange = () => {
  const to = todayIso();
  return { from: shiftMonthsIso(to, -SHIFT_WINDOW_MONTHS), to };
};

// shifts-v2 filter columns, shared by the Shifts and Net Pay lists so the same field can't diverge between them.
// A single-entry `operators` list means no operator menu — the choice is implied (e.g. Pay Type is one-of-two).
export const shiftOperatorFilterColumns = ({ paySchedules = [], includeHours = false, includeShiftType = false }) =>
  [
    {
      // Between only: shifts-v2 scans a bounded window, so a one-sided date filter would need the
      // other bound invented behind the user's back — and a real shift outside it returns nothing.
      key: SHIFT_DATE_FILTER_KEY,
      label: 'Date',
      type: CONTROL_TYPES.DATE,
      operators: [OPERATORS.BETWEEN],
      // Pinned rather than removable: dropping the chip would mean the default window, not "all time".
      maxRangeDays: SHIFT_MAX_RANGE_DAYS,
      // Picking a bound too far from the other drags it back to the same day-of-month, this many months away.
      snapRangeMonths: SHIFT_WINDOW_MONTHS,
      noFuture: true,
      pinned: true,
    },
    {
      key: 'full_name',
      label: 'Employee',
      type: CONTROL_TYPES.TEXT,
      operators: TEXT_MATCH_OPERATORS,
    },
    {
      key: 'employee_code',
      label: 'Employee Code',
      type: CONTROL_TYPES.TEXT,
      operators: TEXT_MATCH_OPERATORS,
    },
    {
      key: 'work_email',
      label: 'Email',
      type: CONTROL_TYPES.TEXT,
      operators: TEXT_MATCH_OPERATORS,
    },
    includeShiftType && {
      // Integration-supplied free text (e.g. TIPS), so no fixed option list.
      key: 'shift_type',
      label: 'Type',
      type: CONTROL_TYPES.TEXT,
      operators: TEXT_MATCH_OPERATORS,
    },
    {
      key: 'source',
      label: 'Source',
      type: CONTROL_TYPES.CHECKBOX,
      options: Object.values(SHIFT_SOURCE).map(value => ({ text: toTitleCase(value), value })),
      operators: [OPERATORS.IN],
    },
    {
      key: 'salary_or_hourly',
      label: 'Pay Type',
      type: CONTROL_TYPES.RADIO,
      options: Object.values(EMPLOYEE_PAY_TYPE).map(value => ({ text: toTitleCase(value), value })),
      operators: [OPERATORS.EQ],
    },
    {
      key: 'pay_schedule_id',
      label: 'Pay Schedule',
      type: CONTROL_TYPES.RADIO,
      options: paySchedules.map(payschedule => ({ text: payschedule.name, value: payschedule.pay_schedule_id })),
      operators: [OPERATORS.EQ, OPERATORS.NE],
      hidden: !paySchedules.length,
    },
    includeHours && {
      key: 'hours',
      label: 'Hours',
      type: CONTROL_TYPES.NUMBER,
      operators: COMPARISON_OPERATORS,
    },
    {
      key: 'wages',
      label: 'Earned Wages',
      type: CONTROL_TYPES.NUMBER,
      operators: COMPARISON_OPERATORS,
    },
  ].filter(Boolean);
